import { flexRender, useTable } from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { LoadingSpinner } from "shared/ui/loading-spinner/LoadingSpinner"

import { ProjectHistoryViewModel } from "../model/useProjectHistoryViewModel"
import "../styles/_project-history-table.scss"
import {
  makeColumns,
  projectHistoryTableFeatures
} from "./ProjectHistoryTableCellRenderers"

export function ProjectHistoryView({
  isLoading,
  rows,
  hasMore,
  loadMore,
  getElementUrl
}: ProjectHistoryViewModel) {
  const { t } = useTranslation()
  const columns = useMemo(
    () => makeColumns(t, getElementUrl),
    [t, getElementUrl]
  )
  const containerRef = useRef<HTMLDivElement>(null)

  const table = useTable({
    features: projectHistoryTableFeatures,
    data: rows,
    columns,
    getRowId: row =>
      row.kind === "entry"
        ? row.entry.id
        : `${row.parentEntry.id}__change__${row.changeIndex}`
  })

  const tableRows = table.getRowModel().rows

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 44,
    overscan: 5
  })

  const virtualRows = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0

  if (isLoading) {
    return (
      <div className="project-history--loading">
        <LoadingSpinner />
      </div>
    )
  }

  if (!isLoading && (!rows || rows.length === 0)) {
    return (
      <div className="project-history__no-results">
        {t("ProjectHistory.NoResults")}
      </div>
    )
  }

  return (
    <div className="project-history">
      <div ref={containerRef} className="project-history__scroll-container">
        <table
          className="project-history-table"
          aria-rowcount={rows.length + 1}
        >
          <colgroup>
            <col className="project-history-col--timestamp" />
            <col className="project-history-col--editor" />
            <col className="project-history-col--entity-type" />
            <col className="project-history-col--entity-id" />
            <col className="project-history-col--field" />
            <col className="project-history-col--old-value" />
            <col className="project-history-col--new-value" />
            <col className="project-history-col--action" />
            <col className="project-history-col--link" />
          </colgroup>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                aria-rowindex={1}
                className="project-history-table__header-row"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={classNames("project-history-table__header-cell")}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden>
                <td style={{ height: paddingTop }} colSpan={columns.length} />
              </tr>
            )}
            {virtualRows.map(virtualRow => {
              const row = tableRows[virtualRow.index]
              return (
                <tr
                  key={row.id}
                  aria-rowindex={virtualRow.index + 2}
                  className={classNames("project-history-table__row", {
                    "project-history-table__row--change-detail":
                      row.original.kind === "changeDetail"
                  })}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={classNames("project-history-table__cell")}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              )
            })}
            {paddingBottom > 0 && (
              <tr aria-hidden>
                <td
                  style={{ height: paddingBottom }}
                  colSpan={columns.length}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore ? (
        <div className="project-history-table__footer">
          <Button
            variant={ButtonVariant.SECONDARY}
            className="project-history__load-more"
            onClick={loadMore}
            label={t("General.LoadMore")}
          />
        </div>
      ) : null}
    </div>
  )
}
